import { errorRuntime } from '@/shared/lib/core/errors';
import { instance } from '@/app/libs/I';
import { Date as MyDate } from '@/app/modules/date/Date';
import { requestService } from '@/app/modules/request/RequestService';
import { bitrixApiService } from '@/app/modules/bitrix/BitrixApiService';
import { bitrixStoreService } from '@/app/modules/bitrix/BitrixStoreService';
import { dateService } from '@/app/modules/date/DateService';
import { profileController } from '@/app/appModules/profile/profileController';
import { useBitrixStore } from '@/shared/store/useBitrixStore';
import { PromisePatron } from '@/app/integration/promise/PromisePatron';
import { GuestDynamic } from '@/app/system/guest/GuestDynamic';
import { SupportRequestsForEmailChange } from '@/app/application/support/SupportRequestsForEmailChange';

export interface BitrixEmailTriesResponse {
  // eslint-disable-next-line camelcase
  current_attempts: number,
  // eslint-disable-next-line camelcase
  total_attempts: number,
  // eslint-disable-next-line camelcase
  time_limit: number,
  deadline: string,
  // eslint-disable-next-line camelcase
  is_client_email_confirmed: boolean,
  // eslint-disable-next-line camelcase
  change_client_email?: string
}

interface BitrixEmailChangeResponse {
  email: string,
  result: string
}

interface BitrixErrorResponse {
  error?: string
}

interface BitrixCodeConfirmResponse extends BitrixErrorResponse {
  result: string
}

interface BitrixEmailConfirmResponse extends BitrixErrorResponse {
  result: string
}

interface BitrixClearChangeResponse {
  result: string
}

interface MessageFormat {
  info: string,
  text: string,
  title: string,
  isError: boolean,
  canCancel: boolean,
  canRepeat: boolean,
  canResend: boolean,
}

/**
 * @deprecated legacy
 */
export namespace bitrixEmailService {
  /**
   * @deprecated legacy
   */
  export async function checkTriesExpired() {
    const bitrixStore = useBitrixStore();
    const { clientId } = bitrixStore;

    if (!clientId) {
      return false;
    }

    const triesResponse = await requestService.applyWithBitrixHandler<BitrixEmailTriesResponse>(bitrixApiService.emailTriesRequest(clientId));
    bitrixStore.setChangeEmail(triesResponse);

    return triesResponse.current_attempts === 0;
  }

  /**
   * @deprecated legacy
   */
  export async function checkTriesExpiredWithError() {
    const bitrixStore = useBitrixStore();
    const isTriesExpired = await checkTriesExpired();
    const { changeEmail } = bitrixStore;
    const { changeClientEmail } = bitrixStore;

    const triesMessage = await buildTriesMessage(changeEmail, String(changeClientEmail));

    if (triesMessage.isError) {
      errorRuntime(triesMessage.text, triesMessage);
    }

    return isTriesExpired;
  }

  /**
   * @deprecated legacy
   */
  export async function buildTriesMessage(
    triesResponse: BitrixEmailTriesResponse,
    changeClientEmail: string,
  ): Promise<MessageFormat> {
    if (triesResponse.is_client_email_confirmed) {
      const createdPatron = new PromisePatron();
      const supportCreated = new SupportRequestsForEmailChange();
      supportCreated.requests(createdPatron, new GuestDynamic((e: Error) => {
        createdPatron.doReject(e);
      }));
      const response = await createdPatron.promise();
      const supportIds = response.map(support => support.id);

      const supportNumber = `Номер заявки: ${supportIds.join(', ')}`;
      return {
        title: '',
        info: `Заявка на изменение e-mail отправлена. ${supportNumber}. Пожалуйста, дождись ее подтверждения. После этого тебе нужно будет заново пройти регистрацию для получения доступа к личному кабинету.`,
        text: `Заявка на изменение e-mail отправлена. ${supportNumber}. Пожалуйста, дождись ее подтверждения. После этого тебе нужно будет заново пройти регистрацию для получения доступа к личному кабинету.`,
        isError: false,
        canCancel: false,
        canRepeat: false,
        canResend: false,
      };
    }

    if (!changeClientEmail) {
      return {
        title: '',
        info: 'Для изменения почты введи новый e-mail',
        text: 'Для изменения почты введи новый e-mail',
        isError: false,
        canCancel: false,
        canRepeat: false,
        canResend: false,
      };
    }

    if (profileController.isEmailChangeRequestByB24()) {
      return {
        title: '',
        info: 'Ты не отправил заявку из личного кабинета, но твой e-mail пытались изменить',
        text: 'Ты не отправил заявку из личного кабинета, но твой e-mail пытались изменить',
        isError: false,
        canCancel: false,
        canRepeat: false,
        canResend: false,
      };
    }

    const now = new Date();
    const deadline = new Date(triesResponse.deadline);

    if (triesResponse.deadline && now > deadline) {
      return {
        title: 'Попробуй снова',
        info: 'Ты не подтвердил изменение e-mail. Чтобы получить новый код подтверждения нажми «Повторить попытку» и введи код из письма, отправленного на твой e-mail. Чтобы отменить заявку нажми «Отмена»',
        text: `Истекло время на подтверждение нового адреса почты: "${changeClientEmail}". Ты можешь ввести новый e-mail для подтверждения или отменить заявку `,
        isError: false,
        canCancel: true,
        canRepeat: false,
        canResend: true,
      };
    }

    const restTries = triesResponse.current_attempts;
    const timeLimit = await dateService.apply<MyDate>(instance(MyDate, triesResponse.deadline));

    if (restTries <= 0) {
      return {
        title: 'Попробуй снова',
        info: `Ты не подтвердил изменение e-mail. Пожалуйста, попробуй запросить новый код подтверждения после ${timeLimit.formattedValue}`,
        text: `Тебе не удалось подтвердить "${changeClientEmail}" за "${triesResponse.total_attempts}" попыток. Новую заявку из своего Личного кабинета ты можешь отправить после "${timeLimit.formattedValue}"`,
        isError: true,
        canCancel: false,
        canRepeat: false,
        canResend: false,
      };
    }

    // Вы не смогли подтвердить имейл, но попытки еще есть
    return {
      title: 'Подтверждение e-mail',
      info: 'Ты не подтвердил изменение e-mail. Для того, чтобы продолжить нажми «Пройти подтверждение» и введи код из письма, отправленного на твой e-mail. Чтобы отменить заявку нажми «Отмена»',
      text: 'Ты не подтвердил изменение e-mail. Для того, чтобы продолжить нажми «Пройти подтверждение» и введи код из письма, отправленного на твой e-mail. Чтобы отменить заявку нажми «Отмена»',
      isError: false,
      canCancel: true,
      canRepeat: true,
      canResend: false,
    };
  }

  /**
   * @deprecated legacy
   */
  export async function changeEmail(newEmail: string) {
    const clientId = await bitrixStoreService.getClientId();
    const response = await requestService.applyWithBitrixHandler<BitrixEmailChangeResponse>(
      bitrixApiService.updateEmail(String(clientId), newEmail),
    );

    return response.result === 'success';
  }

  /**
   * @deprecated legacy
   */
  export async function changeEmailWithError(newEmail: string) {
    const isAccepted = await changeEmail(newEmail);
    if (!isAccepted) {
      errorRuntime('Ошибка при обновлении email');
    }

    const bitrixStore = useBitrixStore();
    bitrixStore.setClientField(['change_client_email', newEmail]);

    return isAccepted;
  }

  /**
   * @deprecated legacy
   */
  export async function sendEmailCode(code: string) {
    const clientId = await bitrixStoreService.getClientId();
    const email = await bitrixStoreService.getClientChangeEmail();
    const response = await requestService.applyWithBitrixHandler<BitrixCodeConfirmResponse>(
      bitrixApiService.sendConfirmCode(
        String(email),
        code,
        String(clientId),
      ));

    if (response.error) {
      errorRuntime(response.error);
    }

    const isConfirmed = response.result === 'success';

    if (isConfirmed) {
      await bitrixStoreService.fetchClientInfo();
    }

    return isConfirmed;
  }

  /**
   * @deprecated legacy
   */
  export async function sendEmailConfirm() {
    const bitrixStore = useBitrixStore();
    const { clientId } = bitrixStore;
    const response = await requestService.applyWithBitrixHandler<BitrixEmailConfirmResponse>(
      bitrixApiService.sendConfirmEmail(String(clientId)),
    );

    if (response.error) {
      errorRuntime(response.error);
    }

    return response.result === 'success';
  }

  /**
   * @deprecated legacy
   */
  export async function sendEmailConfirmWithError() {
    const isSent = await sendEmailConfirm();
    if (!isSent) {
      errorRuntime('Ошибка отправки письма с кодом подтверждения');
    }

    return isSent;
  }

  /**
   * @deprecated legacy
   */
  export async function clearChangeEmail() {
    const clientId = await bitrixStoreService.getClientId();
    return await requestService.applyWithBitrixHandler<BitrixClearChangeResponse>(
      bitrixApiService.clearChangeEmail(String(clientId)),
    );
  }

  /**
   * @deprecated legacy
   */
  export async function clearChangeEmailWithError() {
    const { result } = await clearChangeEmail();
    const cleared = result === 'success';

    if (!cleared) {
      errorRuntime('Не удалось отменить заявку на изменение имейла');
    }

    if (cleared) {
      await bitrixStoreService.fetchClientInfo();
    }

    return cleared;
  }

  /**
   * @deprecated legacy
   */
  export async function replaceEmail() {
    const clientEmail = await bitrixStoreService.getClientEmail();
    const changeCode = await bitrixStoreService.getClientChangeCode();
    const response = await requestService.applyWithBitrixHandler<any>(bitrixApiService.replaceEmail(String(clientEmail), String(changeCode)));

    return response;
  }
}
